import React, { useState } from "react";
import "./App.css";
import plantLabels from "./head-plant-name-classification-labels.json";
import { LanguageProvider, useLanguage } from "./i18n/LanguageContext";

function AppContent() {
  const { texts, language, toggleLanguage } = useLanguage();
  const [file, setFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [leafInfo, setLeafInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlant, setSelectedPlant] = useState("");
  const [useModelInference, setUseModelInference] = useState(true);
  const [inferenceMethodUsed, setInferenceMethodUsed] = useState(null);
  const [plantSelectedForRequest, setPlantSelectedForRequest] = useState("");
  const [skipSegmentation, setSkipSegmentation] = useState(false);
  const [segmentationModel, setSegmentationModel] = useState("sam3");
  const API_BASE_URL = "https://predict.cultivai.com.br";

  const handleCapture = ({ target }) => {
    const fileReader = new FileReader();
    const file = target.files[0];

    fileReader.onloadend = () => {
      setImagePreviewUrl(fileReader.result);
      setLeafInfo(null);
    };

    if (file) {
      fileReader.readAsDataURL(file);
      setFile(file);
    } else {
      setImagePreviewUrl("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!file) return;

    setIsLoading(true);
    setLeafInfo(null);

    setInferenceMethodUsed(useModelInference);
    setPlantSelectedForRequest(selectedPlant);

    const formData = new FormData();
    formData.append("file", file);

    const query = new URLSearchParams({
      skip_segmentation: String(skipSegmentation),
      segmentation_model: segmentationModel,
    }).toString();

    try {
      const response = await fetch(`${API_BASE_URL}/analyze?${query}`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      setLeafInfo(data);
    } catch (error) {
      console.error("Error:", error);
      setLeafInfo({ success: false, error: "Failed to analyze leaf" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app">
      <div className="container">
        <div className="language-toggle">
          <button onClick={toggleLanguage}>
            {language === "en" ? "PT" : "EN"}
          </button>
        </div>
        <h1>{texts.title}</h1>
        <div className="upload-section">
          <div className="form-controls">
            <form onSubmit={handleSubmit}>
              <div className="file-input-container">
                <label className="file-input-label">
                  {texts.chooseFile}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCapture}
                  />
                </label>
              </div>
              <select
                value={selectedPlant}
                onChange={(e) => setSelectedPlant(e.target.value)}
                disabled={useModelInference}
              >
                <option value="">{texts.selectPlant}</option>
                {plantLabels.classes.map((plant) => (
                  <option key={plant} value={plant}>
                    {plant}
                  </option>
                ))}
                <option value="other">{texts.otherOption}</option>
              </select>
              <div className="checkbox-container">
                <label>
                  <input
                    type="checkbox"
                    checked={useModelInference}
                    onChange={(e) => setUseModelInference(e.target.checked)}
                  />
                  {texts.useAI}
                </label>
              </div>
              <div className="checkbox-container">
                <label>
                  <input
                    type="checkbox"
                    checked={skipSegmentation}
                    onChange={(e) => setSkipSegmentation(e.target.checked)}
                  />
                  {texts.skipSegmentation}
                </label>
              </div>
              <select
                value={segmentationModel}
                onChange={(e) => setSegmentationModel(e.target.value)}
                disabled={skipSegmentation}
              >
                <option value="sam">{texts.modelSam}</option>
                <option value="sam2">{texts.modelSam2}</option>
                <option value="sam3">{texts.modelSam3}</option>
                <option value="fastsam">{texts.modelFastSam}</option>
                <option value="yolo">{texts.modelYolo}</option>
                <option value="langsam">{texts.modelLangSam}</option>
              </select>
              <button
                type="submit"
                disabled={
                  !file || isLoading || (!useModelInference && !selectedPlant)
                }
              >
                {isLoading ? texts.analyzing : texts.submit}
              </button>
            </form>
          </div>
          <div className="preview-container">
            {imagePreviewUrl ? (
              <img src={imagePreviewUrl} alt="Leaf Preview" />
            ) : (
              <span style={{ color: "#666" }}>{texts.previewText}</span>
            )}
          </div>
        </div>
        {leafInfo && (
          <div className="results">
            <h2>{texts.resultsTitle}</h2>
            {leafInfo.success ? (
              <>
                {leafInfo.leaf_results && leafInfo.leaf_results.length > 0 ? (
                  <>
                    <p>
                      {texts.leavesDetected}: {leafInfo.num_leaves}
                    </p>
                    <p>
                      {texts.diseasedLeaves}: {leafInfo.num_diseased}
                    </p>
                    <div className="disclaimer">
                      <p>{texts.disclaimer}</p>
                    </div>
                    <div className="leaf-results-grid">
                      {leafInfo.leaf_results.map((leaf, index) => {
                        // Conditionally determine the plant name to display
                        const displayPlantName = inferenceMethodUsed
                          ? leaf.plant_name?.predicted_plant
                          : plantSelectedForRequest;

                        return (
                          <div key={index} className="leaf-result-item">
                            {!skipSegmentation && (
                              <img
                                src={`${API_BASE_URL}${leaf.leaf_image}`}
                                alt={`Leaf ${index + 1}`}
                                className="leaf-image"
                              />
                            )}
                            <p data-label={texts.expert1}>
                              {leaf.is_healthy ? texts.healthy : texts.sick}
                            </p>
                            <p data-label={texts.expert2}>
                              {displayPlantName || texts.notAvailable}
                              {inferenceMethodUsed &&
                                leaf.plant_name?.top_plants?.[0]
                                  ?.probability && (
                                  <span>
                                    {" "}
                                    (
                                    {(
                                      leaf.plant_name.top_plants[0]
                                        .probability * 100
                                    ).toFixed(1)}
                                    %)
                                  </span>
                                )}
                            </p>
                            {!leaf.is_healthy && (
                              <>
                                <p data-label={texts.expert3}>
                                  {leaf.pathogen?.predicted_pathogen ||
                                    texts.notAvailable}
                                  {leaf.pathogen?.top_pathogens?.[0]
                                    ?.probability && (
                                    <span>
                                      {" "}
                                      (
                                      {(
                                        leaf.pathogen.top_pathogens[0]
                                          .probability * 100
                                      ).toFixed(1)}
                                      %)
                                    </span>
                                  )}
                                </p>
                                <p data-label={texts.expert4}>
                                  {leaf.disease?.predicted_disease ||
                                    texts.notAvailable}
                                  {leaf.disease?.top_diseases?.[0]
                                    ?.probability && (
                                    <span>
                                      {" "}
                                      (
                                      {(
                                        leaf.disease.top_diseases[0]
                                          .probability * 100
                                      ).toFixed(1)}
                                      %)
                                    </span>
                                  )}
                                </p>
                                <p data-label={texts.expert5}>
                                  {leaf.symptoms?.top_symptoms
                                    ?.filter(
                                      (symptom) => symptom.probability > 0.1
                                    )
                                    ?.slice(0, 3)
                                    .map((symptom, index) => (
                                      <p key={index}>
                                        {symptom.symptoms} (
                                        {(symptom.probability * 100).toFixed(1)}
                                        %)
                                      </p>
                                    ))}
                                </p>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p>{texts.noLeavesDetected}</p>
                )}
              </>
            ) : (
              <p style={{ color: "red" }}>
                {texts.errorPrefix}: {leafInfo.error || texts.unknownError}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App;
